import * as THREE from 'three';
import { gsap } from 'gsap';

// Canvas
const canvas = document.querySelector('canvas.webgl');

// Scene
const scene = new THREE.Scene();

// Sizes
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight
};

// Camera with enhanced 3D perspective
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 200);
camera.position.set(0, 0, 6);
scene.add(camera);

// Colors
const colors = {
  primary: new THREE.Color('#0e95ff'),
  secondary: new THREE.Color('#09fbd3'),
  accent: new THREE.Color('#ff3e9d'),
  dark: new THREE.Color('#050505'),
  light: new THREE.Color('#ffffff')
};

// Lights
const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
scene.add(ambientLight);

const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
directionalLight.position.set(2, 3, 4);
scene.add(directionalLight);

// Create a group for all shapes
const shapesGroup = new THREE.Group();
scene.add(shapesGroup);

// Low Poly Shapes
const createLowPolyShape = (geometry, material, position, scale) => {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(position.x, position.y, position.z);
  mesh.scale.set(scale, scale, scale);
  mesh.rotation.x = Math.random() * Math.PI;
  mesh.rotation.y = Math.random() * Math.PI;
  mesh.rotation.z = Math.random() * Math.PI;
  return mesh;
};

// Tetrahedron with enhanced 3D positioning
const tetraGeometry = new THREE.TetrahedronGeometry(1, 0);
const tetraMaterial = new THREE.MeshPhongMaterial({
  color: colors.primary,
  wireframe: true,
  transparent: true,
  opacity: 0.9,
  flatShading: true
});
const tetra = createLowPolyShape(tetraGeometry, tetraMaterial, { x: -4, y: 3, z: -5 }, 1.5);
shapesGroup.add(tetra);

// Octahedron
const octaGeometry = new THREE.OctahedronGeometry(1, 0);
const octaMaterial = new THREE.MeshPhongMaterial({
  color: colors.secondary,
  wireframe: true,
  transparent: true,
  opacity: 0.9,
  flatShading: true
});
const octa = createLowPolyShape(octaGeometry, octaMaterial, { x: 5, y: -2, z: 3 }, 1.3);
shapesGroup.add(octa);

// Icosahedron
const icosaGeometry = new THREE.IcosahedronGeometry(1, 0);
const icosaMaterial = new THREE.MeshPhongMaterial({
  color: colors.accent,
  wireframe: true,
  transparent: true,
  opacity: 0.8,
  flatShading: true
});
const icosa = createLowPolyShape(icosaGeometry, icosaMaterial, { x: 0, y: 2.5, z: -4 }, 1.5);
shapesGroup.add(icosa);

// Dodecahedron
const dodecaGeometry = new THREE.DodecahedronGeometry(1, 0);
const dodecaMaterial = new THREE.MeshPhongMaterial({
  color: colors.primary,
  wireframe: true,
  transparent: true,
  opacity: 0.8,
  flatShading: true
});
const dodeca = createLowPolyShape(dodecaGeometry, dodecaMaterial, { x: -2, y: -2, z: -3 }, 0.8);
shapesGroup.add(dodeca);

// Additional shapes for more visual interest
const coneGeometry = new THREE.ConeGeometry(1, 2, 6);
const coneMaterial = new THREE.MeshPhongMaterial({
  color: colors.secondary,
  wireframe: true,
  transparent: true,
  opacity: 0.7,
  flatShading: true
});
const cone = createLowPolyShape(coneGeometry, coneMaterial, { x: 2, y: 1.5, z: -2 }, 0.7);
shapesGroup.add(cone);

const cylinderGeometry = new THREE.CylinderGeometry(0.5, 1, 2, 8);
const cylinderMaterial = new THREE.MeshPhongMaterial({
  color: colors.accent,
  wireframe: true,
  transparent: true,
  opacity: 0.7,
  flatShading: true
});
const cylinder = createLowPolyShape(cylinderGeometry, cylinderMaterial, { x: -1, y: 0, z: -1 }, 0.6);
shapesGroup.add(cylinder);

// Central floating shape
const centralGeometry = new THREE.IcosahedronGeometry(1.5, 1);
const centralMaterial = new THREE.MeshPhongMaterial({
  color: colors.light,
  wireframe: true,
  transparent: true,
  opacity: 0.4,
  flatShading: true
});
const centralShape = new THREE.Mesh(centralGeometry, centralMaterial);
centralShape.position.set(0, 0, -2);
scene.add(centralShape);

// Particles
const particlesCount = 1500;
const positions = new Float32Array(particlesCount * 3);
const particleColors = new Float32Array(particlesCount * 3);

for (let i = 0; i < particlesCount; i++) {
  const i3 = i * 3;
  
  // Position
  positions[i3] = (Math.random() - 0.5) * 20;
  positions[i3 + 1] = (Math.random() - 0.5) * 20;
  positions[i3 + 2] = (Math.random() - 0.5) * 20;
  
  // Color
  const colorArray = [colors.primary, colors.secondary, colors.accent];
  const color = colorArray[Math.floor(Math.random() * colorArray.length)];
  particleColors[i3] = color.r;
  particleColors[i3 + 1] = color.g;
  particleColors[i3 + 2] = color.b;
}

const particlesGeometry = new THREE.BufferGeometry();
particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
particlesGeometry.setAttribute('color', new THREE.BufferAttribute(particleColors, 3));

const particlesMaterial = new THREE.PointsMaterial({
  size: 0.04,
  sizeAttenuation: true,
  transparent: true,
  vertexColors: true,
  blending: THREE.AdditiveBlending,
  depthWrite: false
});

const particles = new THREE.Points(particlesGeometry, particlesMaterial);
scene.add(particles);

// Digital Lines
const linesCount = 15;
const lines = [];

for (let i = 0; i < linesCount; i++) {
  const lineGeometry = new THREE.BufferGeometry();
  const linePoints = [];
  
  // Create random flowing path
  const pointsCount = Math.floor(Math.random() * 6) + 4;
  
  for (let j = 0; j < pointsCount; j++) {
    const x = (Math.random() - 0.5) * 12;
    const y = (Math.random() - 0.5) * 12;
    const z = (Math.random() - 0.5) * 12;
    linePoints.push(new THREE.Vector3(x, y, z));
  }
  
  lineGeometry.setFromPoints(linePoints);
  
  const lineMaterial = new THREE.LineBasicMaterial({
    color: i % 3 === 0 ? colors.primary : i % 3 === 1 ? colors.secondary : colors.accent,
    transparent: true,
    opacity: 0.4
  });
  
  const line = new THREE.Line(lineGeometry, lineMaterial);
  lines.push(line);
  scene.add(line);
}

// Renderer
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  alpha: true,
  antialias: true
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// Animation
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  // Rotate entire shapes group
  shapesGroup.rotation.y = elapsedTime * 0.08;
  shapesGroup.rotation.x = Math.sin(elapsedTime * 0.15) * 0.3;
  
  // Animate individual shapes with different speeds
  tetra.rotation.y = elapsedTime * 0.3;
  tetra.rotation.x = elapsedTime * 0.2;
  
  octa.rotation.x = elapsedTime * 0.4;
  octa.rotation.z = elapsedTime * 0.25;
  
  icosa.rotation.z = elapsedTime * 0.2;
  icosa.rotation.y = elapsedTime * 0.35;
  
  dodeca.rotation.y = -elapsedTime * 0.3;
  dodeca.rotation.x = elapsedTime * 0.15;
  
  cone.rotation.z = elapsedTime * 0.4;
  cone.rotation.y = elapsedTime * 0.2;
  
  cylinder.rotation.x = elapsedTime * 0.25;
  cylinder.rotation.z = elapsedTime * 0.3;
  
  // Animate central shape with pulsing
  centralShape.rotation.y = elapsedTime * 0.12;
  centralShape.rotation.z = elapsedTime * 0.18;
  const pulse = 1 + Math.sin(elapsedTime * 0.6) * 0.3;
  centralShape.scale.set(pulse, pulse, pulse);
  
  // Floating motion for central shape
  centralShape.position.y = Math.sin(elapsedTime * 0.4) * 0.5;
  
  // Animate particles
  particles.rotation.y = elapsedTime * 0.03;
  particles.rotation.x = elapsedTime * 0.02;
  
  // Animate digital lines
  lines.forEach((line, index) => {
    line.rotation.y = elapsedTime * 0.06 * (index % 3 === 0 ? 1 : -1);
    line.rotation.x = elapsedTime * 0.04 * (index % 2 === 0 ? 1 : -1);
    line.rotation.z = elapsedTime * 0.03 * (index % 4 === 0 ? 1 : -1);
  });

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();

// Handle resize
window.addEventListener('resize', () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

// Split title into individual letters and animate
const title = document.querySelector('.title');
const titleText = title.textContent;
title.innerHTML = '';

// Create spans for each letter
titleText.split('').forEach((letter, index) => {
  const span = document.createElement('span');
  span.textContent = letter === ' ' ? '\u00A0' : letter; // Use non-breaking space
  span.classList.add('title-letter');
  title.appendChild(span);
});

// Animate letters flying in from different directions
const letters = document.querySelectorAll('.title-letter');
letters.forEach((letter, index) => {
  // Balanced starting positions - visible but dramatic
  const directions = [
    { x: -800, y: -400, z: -200 },  // From top-left-back
    { x: 800, y: -400, z: 200 },    // From top-right-front
    { x: -600, y: 400, z: 300 },    // From bottom-left-front
    { x: 600, y: 400, z: -300 },    // From bottom-right-back
    { x: 0, y: -800, z: 400 },      // From top-front
    { x: 0, y: 800, z: -400 },      // From bottom-back
    { x: -1000, y: 0, z: 0 },       // From left
    { x: 1000, y: 0, z: 0 },        // From right
    { x: 0, y: 0, z: 500 },         // From front
    { x: 0, y: 0, z: -500 }         // From back
  ];
  
  const startPos = directions[index % directions.length];
  const randomRotX = (Math.random() - 0.5) * 720; // Visible rotation range
  const randomRotY = (Math.random() - 0.5) * 720;
  const randomRotZ = (Math.random() - 0.5) * 720;
  
  // Set initial position (off-screen/3D space)
  gsap.set(letter, {
    x: startPos.x,
    y: startPos.y,
    z: startPos.z,
    rotationX: randomRotX,
    rotationY: randomRotY,
    rotationZ: randomRotZ,
    scale: 0.3,
    opacity: 1
  });
  
  // Animate to final position with enhanced 3D trajectory
  gsap.to(letter, {
    x: 0,
    y: 0,
    z: 0,
    rotationX: 0,
    rotationY: 0,
    rotationZ: 0,
    scale: 1,
    opacity: 1,
    duration: 2 + Math.random() * 1,
    delay: 0.2 + (index * 0.15),
    ease: 'power2.out',
    onComplete: () => {
      // Add subtle floating animation after letters settle
      gsap.to(letter, {
        y: Math.sin(index * 0.5) * 2,
        rotationZ: Math.sin(index) * 2,
        duration: 3 + Math.random() * 2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: Math.random() * 2
      });
    }
  });
});

// Cursor interaction for immersive experience
document.addEventListener('mousemove', (event) => {
  const mouseX = (event.clientX / sizes.width) * 2 - 1;
  const mouseY = -(event.clientY / sizes.height) * 2 + 1;
  
  // Subtle camera movement
  gsap.to(camera.position, {
    x: mouseX * 0.5,
    y: mouseY * 0.5,
    duration: 2,
    ease: 'power2.out'
  });
  
  // Interactive shape rotation
  gsap.to(shapesGroup.rotation, {
    x: mouseY * 0.15,
    y: mouseX * 0.15,
    duration: 2,
    ease: 'power2.out'
  });
  
  // Particle interaction
  gsap.to(particles.rotation, {
    x: mouseY * 0.05,
    y: mouseX * 0.05,
    duration: 3,
    ease: 'power2.out'
  });
});